import { ReactElement, useEffect } from "react";
import { RequestFieldDto } from "../Schemas/RequestFieldDto";
import { RequestParameterTypeEnum } from "../Enums/RequestParameterTypeEnum";
import { Alert, Autocomplete, Box, Checkbox, CircularProgress, FormControlLabel, InputAdornment, MenuItem, SxProps, TextField, TextFieldProps, Theme, Tooltip, Typography, IconButton, useTheme } from "@mui/material";
import { Controller, ControllerFieldState, ControllerProps, ControllerRenderProps, FieldValues, useFormContext, UseFormStateReturn } from "react-hook-form";
import { getCenteredSpaceBetweenStyles, useAbortControllers, useTask, parseNumber, useRerender, DocumentsIcon, InfoIcon, ExclamationTriangleIcon, EuroIcon, formatNumberGroups } from "vig-ui-library";
import dayjs from 'dayjs';
import { DatePicker } from "@mui/x-date-pickers";
import { RequestFormContext } from "../RequestFormContext";
import { RequestHelperGetContractPrepopInfoInputSchema } from "../Schemas/RequestHelperGetContractPrepopInfoInput";
import { getFieldsWithType } from "../Utilities/filters";
import { prepopulateContractAddressFieldValues } from "../Utilities/mappers";
import { RequestHelperGetPartnerPrepopInfoInputSchema } from "../Schemas/RequestHelperGetPartnerPrepopInfoInput";
import { handleDevelopmentError } from "../Utilities/error";
import { RequestSourceTypeEnum } from "../Enums/RequestSourceTypeEnum";
import { DisplayTextDisplayTypeEnum } from "../Enums/DisplayTextDisplayTypeEnum";
import { NoticeDisplayTypeEnum } from "../Enums/NoticeDisplayTypeEnum";
import { getIdentificatorHeadingForField } from "../Utilities/identificatorHeading";
import { alpha } from "@mui/material/styles";
import { EmailSuggestionBox } from "./EmailSuggestionBox";
import { getContainerFocusOutlineSx } from "../../focusStyles";
import { normalizeEmail, normalizePhone, isTypeOfFieldIdentificator, isTypeOfFieldEmail, isTypeOfFieldPhone, isCheckboxTrue  } from "../Utilities/formUtils";

export type FieldProps = RequestFormContext & {
    field: RequestFieldDto;
    sectionContainIdentificatorFieldWithOtherField: boolean;
};

type RenderFieldParams = {
    field: ControllerRenderProps<FieldValues, string>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<FieldValues>;
}

export const Field = (props: FieldProps) => {
    const theme = useTheme();

    const { field, settings, requestApi, request } = props;

    const { control, formState, resetField, setValue } = useFormContext();

    const { controlledCall, abortAll } = useAbortControllers();

    const isClientRequest = request.current.sourceTypeId === RequestSourceTypeEnum.KLIENT;

    const rerender = useRerender();

    useEffect(() => {
        return abortAll;
    }, []);

    const getMinWidth = (): string | number => {
        switch (field.uiHint) {
            case "street":
            case "contractstreet":
            case "city":
            case "contractcity":
            case "country":
            case "contractcountry":
                return 200;
            case "streetnum":
            case "contractstreetnum":
            case "postcode":
            case "contractpostcode":
                return 140;
            default:
                return "unset";
        }
    }

    /// --------------------------------------------------------------------
    /// Handling changes methods
    /// --------------------------------------------------------------------
    const saveAfterFieldChange = (debounced: boolean = true) => {
        props.onlySaveRequest(debounced, false, undefined, field.fieldsRelationMaster === true);
    }

    const handleChange = (value: any) => {
        field.paramValue = value;
        rerender();
        saveAfterFieldChange();
        handleSpecialChange();
    }

    const handleSpecialChange = () => {
        if (field.key === 'allcontracts') {
            settings.current.isBulkChangeOnContracts = field.paramValue ?? false;
        }
    }

    const prepopAddressesAndVehicles = async () => {
        const prepopInput = RequestHelperGetContractPrepopInfoInputSchema.parse({
            poistkaId: parseInt(field.paramValue),
            isVehicle: getFieldsWithType(request.current, RequestParameterTypeEnum.VEHICLE).length > 0,
            needClientEmail: getFieldsWithType(request.current, RequestParameterTypeEnum.CLIENT_EMAIL).length > 0,
        });
        const prepopResult = await controlledCall(({ signal }) => requestApi.getRequestContractPrepopInfo(prepopInput, { signal }));
        request.current.fields = prepopulateContractAddressFieldValues(request.current.fields, prepopResult);

        const contractAddressUiHints = ['contractstreet', 'contractstreetnum', 'contractcity', 'contractpostcode', 'contractcountry'];
        request.current.fields
            .filter((f) => contractAddressUiHints.includes(f.uiHint ?? ''))
            .forEach((f) => resetField(f.id, { defaultValue: f.paramValue ?? "" }));

        const availableVehicles = prepopResult.contractVehicles;
        settings.current = {
            ...settings.current,
            availableVehicles,
            odvetvieId: prepopResult.contractOdvetvieId || 0,
            clientEmail: prepopResult.clientEmail || "",
        }

        const vehicleFields = getFieldsWithType(request.current, RequestParameterTypeEnum.VEHICLE);
        vehicleFields.forEach((field) => {
            if (availableVehicles.length === 1) {
                field.paramValue = availableVehicles[0].rowId;
                resetField(field.id, { defaultValue: availableVehicles[0].rowId });
            } else {
                field.paramValue = null;
                resetField(field.id, { defaultValue: null });
            }
        });
    }

    const onContractNoChangeTask = useTask(async () => {
        try {
            if (field.isDocumentRecordId && field.paramValue) {
                const notDocumentRecords = request.current.fields
                    .filter((field) => field.isDocumentRecordId === false && field.type === RequestParameterTypeEnum.POISTKA_ID);

                notDocumentRecords.forEach((field) => {
                    field.paramValue = null;
                });

                const radioButtons = getFieldsWithType(request.current, RequestParameterTypeEnum.RADIO_BUTTON);

                radioButtons.forEach((field) => {
                    if (field.group) {
                        request.current.itemsByGroup[field.group].groupValue = null;
                    }
                    field.paramValue = null;
                });

                await prepopAddressesAndVehicles();

                props.rerenderForm();
            }

            saveAfterFieldChange();
        } catch (err) {
            /// Don't want to show error to user
            handleDevelopmentError(err);
        }
    });

    const handleSearchContractChangeTextfield = (value: any) => {
        field.paramValue = value;
        onContractNoChangeTask.debouncedPerform();
    }

    const onPartnerIdChangeTask = useTask(async () => {
        try {
            if (field.isDocumentRecordId && field.paramValue) {
                const prepopInput = RequestHelperGetPartnerPrepopInfoInputSchema.parse({
                    partnerId: field.paramValue,
                    needClientEmail: getFieldsWithType(request.current, RequestParameterTypeEnum.CLIENT_EMAIL).length > 0,
                });
                const prepopResult = await requestApi.getRequestPartnerPrepopInfo(prepopInput);
                settings.current = { ...settings.current, clientEmail: prepopResult.clientEmail || "" };
                props.rerenderForm();
            }

            saveAfterFieldChange();
        } catch (err) {
            /// Don't want to show error to user
            handleDevelopmentError(err);
        }
    });

    const handlePartnerIdChangeTextfield = (value: any) => {
        const parsedValue = parseNumber(value);
        if (parsedValue === null) return;
        field.paramValue = parsedValue;
        onPartnerIdChangeTask.debouncedPerform();
    }

    useEffect(() => {
        if (!field.prefilledFromQueryParam || !field.paramValue) return;
        field.prefilledFromQueryParam = false;

        if (field.type === RequestParameterTypeEnum.POISTKA_ID) {
            handleSearchContractChangeTextfield(field.paramValue);
        } else if (field.type === RequestParameterTypeEnum.PARTNER_ID) {
            handlePartnerIdChangeTextfield(field.paramValue);
        } else {
            handleChange(field.paramValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /// --------------------------------------------------------------------
    /// Sharable props for all fields and methods to render them
    /// --------------------------------------------------------------------
    const sharedControllerProps: Omit<ControllerProps, "render"> = {
        control: control,
        name: field.id,
        defaultValue: field.paramValue,
        rules: {
            required: field.required && !field.hidden,
            minLength: field.minLength || undefined,
            maxLength: field.maxLength || undefined,
        }
    }

    const getErrorMessage = (): string => {
        const foundError = formState.errors[sharedControllerProps.name];

        if (!foundError) return "";

        const { message, type } = foundError;

        if (type === "required") return "Toto pole je povinné";

        if (type === "invalidId" && message) return message.toString();

        if (type === "maxLength") return `Pole môže obsahovať maximálne ${field.maxLength} znakov`;

        if (type === "minLength") return `Pole musí obsahovať minimálne ${field.minLength} znakov`;

        return "";
    }

    const styles: SxProps<Theme> = settings.current.previewMode ? {
        "& .MuiInputBase-root.MuiFilledInput-root": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            "& .MuiInputBase-input ": {
                WebkitTextFillColor: theme.palette.text.secondary,
                color: theme.palette.text.secondary,
            }
        }
    } : {}

    const isIdentificatorField = isTypeOfFieldIdentificator(field.type);
    const isEmailField = isTypeOfFieldEmail(field.type);
    const isPhoneField = isTypeOfFieldPhone(field.type);

    const tryNormalizeValue = (value: string) => {
        if (isEmailField) return normalizeEmail(value);
        if (isPhoneField) return normalizePhone(value);
        return value;
    }

    const sharedTextFieldProps = (params: RenderFieldParams): TextFieldProps => {
        return {
            fullWidth: true,
            size: "small",
            label: field.parameterText || "",
            ...params.field,
            value: params.field.value || "",
            required: field.required && !field.hidden,
            error: !!formState.errors[sharedControllerProps.name],
            helperText: getErrorMessage(),
            disabled: field.isDisabled || settings.current.previewMode,
            name: field.id,
            variant: "filled",
            sx: styles,
            onChange: (e) => {
                var val = tryNormalizeValue(e.target.value);
                params.field.onChange(val);
                handleChange(val);
            },
        }
    }

    let fieldInput: ReactElement | null = null

    /// --------------------------------------------------------------------
    /// Text fields & Email fields
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.SHORT_TEXT || field.type === RequestParameterTypeEnum.LONG_TEXT || isEmailField || isPhoneField) {
        fieldInput = <Controller
            {...sharedControllerProps}
            render={(params) => <TextField {...sharedTextFieldProps(params)} />}
        />
    }

    /// --------------------------------------------------------------------
    /// Number field
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.NUMBER) {
        fieldInput = <Controller
            {...sharedControllerProps}
            render={(params) => <TextField
                {...sharedTextFieldProps(params)}
                type="number"
                onChange={(e) => {
                    if (field.maxLength && e.target.value.length <= field.maxLength) {
                        params.field.onChange(e);
                        handleChange(e.target.value);
                    }
                }}
            />}
        />
    }

    /// --------------------------------------------------------------------
    /// Money (euro) field - digits only, grouped by spaces, with a
    /// permanent € suffix. The stored/sent value is the formatted string
    /// (e.g. "1 234 567").
    /// --------------------------------------------------------------------
    /// --------------------------------------------------------------------
    /// Money (euro) field - digits only, grouped by spaces, with a
    /// permanent € suffix. The stored value is the raw digit string.
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.MONEY) {
        fieldInput = <Controller
            {...sharedControllerProps}
            render={(params) => {
                const baseProps = sharedTextFieldProps(params);
                return <TextField
                    {...baseProps}
                    value={formatNumberGroups(params.field.value)}
                    inputMode="numeric"
                    onKeyDown={(e) => ["e", "E", "+", "-", ".", ","].includes(e.key) && e.preventDefault()}
                    onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        if (field.maxLength && digits.length > field.maxLength) return;
                        params.field.onChange(digits);
                        handleChange(digits);
                    }}
                    InputProps={{
                        disableUnderline: true,
                        fullWidth: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <EuroIcon width={16} height={16} color={theme.palette.text.primary} />
                            </InputAdornment>
                        ),
                    }}
                />
            }}
        />
    }

    /// --------------------------------------------------------------------
    /// Date field
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.DATE) {
        fieldInput = <Controller
            {...sharedControllerProps}
            defaultValue={field.paramValue ? dayjs(field.paramValue) : null}
            rules={{
                required: field.required && !field.hidden,
            }}
            render={(params) => {
                const { value, onChange, ...otherProps } = sharedTextFieldProps(params);
                return <DatePicker
                    value={params.field.value ? dayjs(params.field.value) : undefined}
                    disabled={field.isDisabled || settings.current.previewMode}
                    defaultValue={field.paramValue ? dayjs(field.paramValue) : null}
                    format="DD.MM.YYYY"
                    slotProps={{ textField: otherProps }}
                    onChange={(value: dayjs.Dayjs | null) => {
                        const serializedValue = value ? value.toISOString() : "";
                        params.field.onChange(serializedValue);
                        handleChange(serializedValue);
                    }}
                />
            }}
        />
    }

    /// --------------------------------------------------------------------
    /// Insurance No / Partner Id - Text Field for non client request
    /// --------------------------------------------------------------------
    if ((field.type === RequestParameterTypeEnum.POISTKA_ID || field.type === RequestParameterTypeEnum.PARTNER_ID) && !isClientRequest) {
        fieldInput = <Controller
            {...sharedControllerProps}
            render={(params) => <TextField
                {...sharedTextFieldProps(params)}
                type="number"
                onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                onChange={(e) => {
                    if (!(field.maxLength && e.target.value.length <= field.maxLength)) return;
                    params.field.onChange(e);
                    if (field.type === RequestParameterTypeEnum.POISTKA_ID) handleSearchContractChangeTextfield(e.target.value);
                    if (field.type === RequestParameterTypeEnum.PARTNER_ID) handlePartnerIdChangeTextfield(e.target.value);
                }}
            />}
        />
    }

    const availableContracts = settings.current.availableContracts;

    useEffect(() => {
        if (field.type === RequestParameterTypeEnum.POISTKA_ID && isClientRequest) {
            field.isDisabled = settings.current.isBulkChangeOnContracts;
            if (field.isDisabled && availableContracts.length > 0 && !availableContracts.some(x => x.id === field.paramValue)) {
                var autoCompleteValue = availableContracts[0].id;
                setValue(sharedControllerProps.name, autoCompleteValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                });
                handleChange(autoCompleteValue);
            }
        }
    }, [settings.current.isBulkChangeOnContracts]);

    /// --------------------------------------------------------------------
    /// Insurance No - Select Field for client request
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.POISTKA_ID && isClientRequest) {

        const contractOptions = availableContracts.map((contract) => ({
            label: `${contract.id} - ${contract.product ?? ""} (${contract.titleToShow ?? ""})${contract.stavToShow ?? ""}`,
            value: contract.id,
            vehicleSublabel: contract.vehicles?.length === 1 ? contract.vehicles[0].vehicleRowData : null,
        }));

        fieldInput = <Controller
            {...sharedControllerProps}
            rules={{ required: field.required && !field.hidden }}
            render={(params) => {
                const selectedContract = contractOptions.find((option) => String(option.value) === String(params.field.value));

                return <Autocomplete
                    sx={{ width: "100%" }}
                    options={contractOptions}
                    noOptionsText="Nie je k dispozícií žiadna zmluva"
                    value={(selectedContract ?? null) as typeof selectedContract}
                    disabled={field.isDisabled || settings.current.previewMode}
                    disableClearable
                    isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
                    onChange={(_e, option) => {
                        const value = option?.value ?? null;
                        params.field.onChange(value);
                        handleSearchContractChangeTextfield(value)
                    }}
                    renderOption={(optionProps, option) => {
                        const { key, ...restOptionProps } = optionProps;
                        return (
                            <li key={key} {...restOptionProps}>
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Typography variant="body2">{option.label}</Typography>
                                    {option.vehicleSublabel && (
                                        <Typography variant="caption" color="text.secondary">
                                            {option.vehicleSublabel}
                                        </Typography>
                                    )}
                                </Box>
                            </li>
                        );
                    }}
                    renderInput={(inputParams) => {
                        const { value, onChange, ...otherProps } = sharedTextFieldProps(params);
                        return <TextField {...inputParams} {...otherProps} />
                    }}
                />
            }}
        />
    }

    /// --------------------------------------------------------------------
    /// Checkbox field
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.CHECKBOX) {
        const isDisabled = field.isDisabled || settings.current.previewMode;

        fieldInput = <Controller
            {...sharedControllerProps}
            render={(params) => {
                const isChecked = isCheckboxTrue(params.field.value ?? field.paramValue);
                const showInlineError = !(field.atLeastOneRequiredWithinGroup && !field.required);
                const errorMessage = showInlineError ? getErrorMessage() : "";
                const hasError = !!errorMessage;

                return <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                    <Box sx={{
                        width: "100%",
                        minHeight: "54px",
                        borderRadius: "7px",
                        border: `1px solid ${hasError ? theme.palette.error.main : isChecked ? theme.palette.common.black : alpha(theme.palette.common.black, 0.2)}`,
                        backgroundColor: isChecked ? theme.palette.grey[50] : "transparent",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "16px",
                        transition: "border-color 0.2s ease, background-color 0.2s ease",
                        "&:hover": !isDisabled ? {
                            borderColor: hasError ? theme.palette.error.main : theme.palette.common.black,
                            backgroundColor: theme.palette.grey[50],
                        } : undefined,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                        onClick={() => {
                            if (isDisabled) return;
                            const nextValue = !isChecked;
                            params.field.onChange(nextValue);
                            handleChange(nextValue);
                        }}
                    >
                        <FormControlLabel
                            sx={{
                                margin: 0,
                                width: "100%",
                                "& .MuiFormControlLabel-label": {
                                    width: "100%",
                                },
                            }}
                            onClick={(e) => e.stopPropagation()}
                            label={<Typography
                                component="span"
                                sx={{
                                    fontFamily: theme.typography.fontFamily,
                                    fontWeight: 500,
                                    fontSize: "15px",
                                    lineHeight: "148%",
                                    letterSpacing: 0,
                                    color: theme.palette.common.black,
                                    pointerEvents: "none",
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {field.parameterText}
                            </Typography>}
                            disabled={isDisabled}
                            checked={isChecked}
                            control={<Checkbox
                                sx={{ padding: 0, marginRight: "10px" }}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                    params.field.onChange(e.target.checked);
                                    handleChange(e.target.checked);
                                }}
                                inputProps={{
                                    onKeyDown: (e) => {
                                        if (e.key === "Enter" && !isDisabled) {
                                            e.preventDefault();
                                            const nextValue = !isChecked;
                                            params.field.onChange(nextValue);
                                            handleChange(nextValue);
                                        }
                                    },
                                }}
                            />}
                        ></FormControlLabel>
                    </Box>
                    {hasError && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                            {errorMessage}
                        </Typography>
                    )}
                </Box>;
            }}
        />
    }

    /// --------------------------------------------------------------------
    /// Address field - with options
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.ADDRESS_TEXT) {
        fieldInput = <Controller
            {...sharedControllerProps}
            render={(params) => {
                const availableAddresses = settings.current.availableAddresses;
                return <TextField {...sharedTextFieldProps(params)} select={isClientRequest}>
                    {!availableAddresses.length && (
                        <MenuItem><em>Nie je k dispozícií žiadna adresa</em></MenuItem>
                    )}
                    {availableAddresses.map((address, index) => {
                        return <MenuItem key={index} value={address.addressToSave || ""}>{address.addressToShow}</MenuItem>
                    })}
                </TextField>
            }}
        />
    }

    /// --------------------------------------------------------------------
    /// Vehicle field
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.VEHICLE) {
        const availableVehicles = settings.current.availableVehicles;
        const vehicleOptions = availableVehicles.map((vehicle) => ({ label: vehicle.vehicleRowData, value: vehicle.rowId }));

        fieldInput = <Controller
            {...sharedControllerProps}
            rules={{ required: field.required && !field.hidden }}
            render={(params) => {
                const selectedVehicle = vehicleOptions.find((option) => String(option.value) === String(params.field.value));

                return <Autocomplete
                    sx={{ width: "100%" }}
                    options={vehicleOptions}
                    value={(selectedVehicle ?? null) as typeof selectedVehicle}
                    disabled={field.isDisabled || settings.current.previewMode}
                    disableClearable
                    isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
                    onChange={(_e, option) => {
                        const value = option?.value ?? null;
                        params.field.onChange(value);
                        handleChange(value);
                    }}
                    renderInput={(inputParams) => {
                        const { value, onChange, ...otherProps } = sharedTextFieldProps(params);
                        return <TextField {...inputParams} {...otherProps} />
                    }}
                />
            }}
        />
    }

    /// --------------------------------------------------------------------
    /// Informative (non-input) elements - rendered directly, not through the
    /// input wrappers and not registered with react-hook-form.
    /// --------------------------------------------------------------------
    if (field.type === RequestParameterTypeEnum.DISPLAY_TEXT) {
        return <DisplayTextField field={field} />;
    }

    if (field.type === RequestParameterTypeEnum.NOTICE) {
        return <NoticeField field={field} />;
    }

    if (!fieldInput) return null;

    const shouldUseIdentificatorFieldWrapper = isIdentificatorField && !props.sectionContainIdentificatorFieldWithOtherField;

    const fieldBox = <Box className="field-box" sx={{ ...getCenteredSpaceBetweenStyles(), gap: 1, width: "100%", flexGrow: 1, flexBasis: getMinWidth() }}>
        {shouldUseIdentificatorFieldWrapper && <IdentificatorFieldWrapper
            isRunning={onContractNoChangeTask.isRunning || onPartnerIdChangeTask.isRunning}
            {...props}
            field={field}
        >
            {fieldInput}
        </IdentificatorFieldWrapper>}

        {!shouldUseIdentificatorFieldWrapper && <FieldWrapper {...props} field={field}>
            {fieldInput}
        </FieldWrapper>}

        {field.description && (
            <Tooltip title={field.description}>
                <InfoIcon width={16} height={16} color={theme.palette.info.main} />
            </Tooltip>
        )}
    </Box>

    if (isEmailField) {
        return <Box className="email-box" sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {fieldBox}
            <EmailSuggestionBox
                field={field}
                setValue={(v) => {
                    setValue(sharedControllerProps.name, v, {
                        shouldDirty: true,
                        shouldValidate: true,
                    });
                    handleChange(v);
                }}
            />
            {field.type === RequestParameterTypeEnum.CLIENT_EMAIL && settings.current.clientEmail && !settings.current.previewMode && (
                <Alert severity="info" sx={{ mt: 2, padding: "16px", ".MuiAlert-message": { padding: 0 } }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Box sx={{ marginTop: "6px" }}>
                            <InfoIcon width={16} height={16} color={theme.palette.info.dark} />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography variant="subtitle1" color="text.primary">
                                Pokiaľ nevyplníte kontaktný email klienta, notifikácia bude odoslaná na:
                            </Typography>

                            <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
                                <Typography variant="subtitle1" color="text.primary" fontWeight="bold">
                                    {settings.current.clientEmail}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Alert>
            )}
        </Box>
    }
    /*
    else if (isPhoneField) {
        return <Box className="email-box" sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {fieldBox}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    width: "100%",
                    gap: 2,
                    mt: 0.5,
                }}
            >
                <Typography variant="body2" sx={{ flex: 1 }}>
                    Zadajte telefónne číslo s predvoľbou krajiny, napr. +421XXXXXXXXX.
                </Typography>
            </Box>
        </Box>
    }
    */

    return fieldBox;
}

const IdentificatorFieldWrapper = (props: FieldProps & { children: ReactElement, isRunning: boolean }) => {
    const theme = useTheme();
    const identificatorHeading = getIdentificatorHeadingForField(props.field.type);

    return <Box
        className="field-holder"
        sx={{
            width: "100%",
            backgroundColor: theme.palette.background.default,
            borderRadius: "7px",
        }}
    >
        <Box sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            padding: theme.spacing(3),
            gap: theme.spacing(2),
            borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
            <DocumentsIcon width={32} height={32} color={theme.palette.primary.main} />
            {identificatorHeading && (
                <Typography variant="h6" component="p">
                    {identificatorHeading}
                </Typography>
            )}
        </Box>

        <Box sx={{ padding: "24px" }}>
            <Box sx={{ ...getCenteredSpaceBetweenStyles(), gap: 2 }}>
                {props.children}
                {props.isRunning && <CircularProgress size={18} />}
            </Box>
        </Box>
    </Box>
}

const FieldWrapper = (props: FieldProps & { children: ReactElement }) => {
    return <Box className="field-holder" sx={{ flexGrow: 1, display: "flex", alignItems: "flex-start" }}>
        {props.children}
    </Box>
}

/// Safely turns a field value (typed as `any`) into a display string.
const getFieldValueText = (value: unknown): string =>
    value === null || value === undefined ? "" : String(value);

/// Figma text styles for DISPLAY_TEXT. Colors reuse the pkp theme tokens
/// (text.secondary = #4D4D4D, text.primary = #000, grey.600 = #666); the
/// "Juli"/"Juli Sans" font family is inherited from the theme typography.
const DISPLAY_TEXT_FONT_FEATURES = "'liga' off, 'clig' off";

const displayTextSx = {
    verticalLabel: {
        color: "text.secondary",
        fontSize: "15px",
        fontWeight: 400,
        lineHeight: "normal",
        fontFeatureSettings: DISPLAY_TEXT_FONT_FEATURES,
    },
    verticalValue: {
        color: "text.primary",
        fontSize: "18px",
        fontWeight: 700,
        lineHeight: "normal",
        fontFeatureSettings: DISPLAY_TEXT_FONT_FEATURES,
    },
    horizontalLabel: {
        color: "text.primary",
        fontSize: "15px",
        fontWeight: 400,
        lineHeight: "148%",
        fontFeatureSettings: DISPLAY_TEXT_FONT_FEATURES,
    },
    horizontalValue: {
        color: "text.primary",
        fontSize: "15px",
        fontWeight: 500,
        lineHeight: "148%",
        fontFeatureSettings: DISPLAY_TEXT_FONT_FEATURES,
    },
    heading: {
        color: "grey.600",
        fontSize: "13px",
        fontWeight: 500,
        lineHeight: "120%",
        letterSpacing: "0.39px",
        textTransform: "uppercase",
        fontFeatureSettings: DISPLAY_TEXT_FONT_FEATURES,
    },
} as const;

/// --------------------------------------------------------------------
/// DISPLAY_TEXT (type 16) - read-only informative text.
///  - Vertical info      (displayType 1): parameterText = label, paramValue = value (stacked)
///  - Horizontal info    (displayType 2): parameterText = label, paramValue = value (inline)
///  - Info block heading (displayType 3): paramValue, falls back to parameterText
/// --------------------------------------------------------------------
const DisplayTextField = ({ field }: { field: RequestFieldDto }) => {
    const label = field.parameterText ?? "";
    const value = getFieldValueText(field.paramValue);

    if (field.displayType === DisplayTextDisplayTypeEnum.INFO_BLOCK_HEADING) {
        return <Typography component="p" sx={{ ...displayTextSx.heading, width: "100%" }}>
            {value || label}
        </Typography>;
    }

    if (field.displayType === DisplayTextDisplayTypeEnum.HORIZONTAL_INFO) {
        return <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, width: "100%" }}>
            <Typography component="span" sx={displayTextSx.horizontalLabel}>{label}</Typography>
            <Typography component="span" sx={{ ...displayTextSx.horizontalValue, textAlign: "right" }}>{value}</Typography>
        </Box>;
    }

    // Default / displayType 1 (Vertical info) - no gap between label and value, width fits content.
    return <Box sx={{ display: "flex", flexDirection: "column", width: "fit-content" }}>
        <Typography component="span" sx={displayTextSx.verticalLabel}>{label}</Typography>
        <Typography component="span" sx={displayTextSx.verticalValue}>{value}</Typography>
    </Box>;
}

/// --------------------------------------------------------------------
/// NOTICE (type 17) - informational box. Uses paramValue, falls back to
/// parameterText. displayType 1 = info (blue), displayType 2 = warning (orange).
/// --------------------------------------------------------------------
const NoticeField = ({ field }: { field: RequestFieldDto }) => {
    const theme = useTheme();
    const content = getFieldValueText(field.paramValue) || (field.parameterText ?? "");

    if (!content) return null;

    const isWarning = field.displayType === NoticeDisplayTypeEnum.WARNING;
    const iconColor = isWarning ? theme.palette.warning.dark : theme.palette.info.dark;

    return <Alert
        severity={isWarning ? "warning" : "info"}
        icon={false}
        sx={{ width: "100%", padding: "16px", ".MuiAlert-message": { padding: 0, width: "100%" } }}
    >
        <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ marginTop: "2px" }}>
                {isWarning
                    ? <ExclamationTriangleIcon width={16} height={16} color={iconColor} />
                    : <InfoIcon width={16} height={16} color={iconColor} />}
            </Box>
            <Typography variant="subtitle1" color="text.primary" sx={{ flex: 1 }}>{content}</Typography>
        </Box>
    </Alert>;
}
